import { Link } from "react-router-dom";
import { ArrowRight, Building2, ShoppingBag, ShieldCheck, Sparkles, Store, FolderArchive, TrendingUp, Wallet, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoOrText } from "@/lib/logo";
import FloatingWhatsAppButton from "@/components/FloatingWhatsAppButton";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <div className="absolute inset-0 grid-bg-lg opacity-40 pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <LogoOrText className="h-10 max-w-[220px]" textClass="font-heading font-black tracking-tighter text-lg" />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to="/lojista/login">
              <Button data-testid="landing-lojista-btn" className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-10">
                <Store size={14} className="mr-2" />
                Portal do Lojista
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 label-tag mb-4 border border-border px-3 py-1.5 rounded-full">
              <Sparkles size={12} /> Ecossistema digital exclusivo
            </div>
            <h1 className="font-heading font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.9] break-anywhere">
              VM Piabetá<br />
              Shopping Center<br />
              <span className="text-muted-foreground">— Hub Digital do Lojista</span>
            </h1>
            <p className="mt-8 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Inovação, modernidade e transparência na gestão do nosso espaço. Um ecossistema completo projetado
              para potencializar o seu negócio, com <strong className="text-foreground">inteligência de rateio</strong>,
              <strong className="text-foreground"> central de documentos</strong>,
              <strong className="text-foreground"> regras de convivência</strong> e
              <strong className="text-foreground"> autoatendimento financeiro</strong> na palma da mão.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <Link to="/quem-somos" data-testid="hero-quem-somos-btn">
                <Button size="lg" className="rounded-sm h-14 px-6 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 hover:-translate-y-0.5 shadow-md hover:shadow-lg transition-all duration-200 border-0">
                  <Info size={16} className="mr-2" />
                  Quem Somos
                </Button>
              </Link>
              <Link to="/lojista/login">
                <Button data-testid="hero-lojista-cta" size="lg" className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-14 px-8 text-base">
                  <Store size={18} className="mr-2" />
                  Acessar Portal do Lojista
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Acesso via token dinâmico enviado por WhatsApp — sem senhas complexas.
            </p>
          </div>

          <div className="lg:col-span-4">
            <div className="border border-border bg-card p-6 space-y-4">
              <div className="label-tag">Nosso empreendimento</div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="stat-num text-5xl">65</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Pontos Comerciais Planejados</div>
                </div>
                <div>
                  <div className="stat-num text-5xl">30</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Estandes Inteligentes em Contêiner</div>
                </div>
                <div>
                  <div className="stat-num text-5xl">35</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Lojas Âncoras e Satélites</div>
                </div>
                <div>
                  <div className="stat-num text-5xl">2</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Frentes de rua ativas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="relative z-10 border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-2xl mb-12">
            <div className="label-tag mb-2">Feito para o lojista do VM Piabetá</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl leading-tight break-anywhere">
              Sua operação, transparente e no seu tempo.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Wallet,
                title: "Autoatendimento financeiro",
                desc: "Pague Pix, cartão ou boleto direto no portal. Consulte seu extrato aberto com aluguel + rateio + IPTU discriminado.",
              },
              {
                icon: FolderArchive,
                title: "Central de documentos e regras",
                desc: "Regimento interno, contrato assinado, histórico de reajustes e comunicados oficiais sempre acessíveis.",
              },
              {
                icon: TrendingUp,
                title: "Solicite obras e prestadores",
                desc: "Envie projetos de reforma para aprovação digital e cadastre operários — a portaria libera automaticamente.",
              },
            ].map((f) => {
              const Ic = f.icon;
              return (
                <div key={f.title} className="stat-block p-6 rounded-sm">
                  <div className="w-10 h-10 bg-foreground text-background flex items-center justify-center rounded-sm">
                    <Ic size={18} />
                  </div>
                  <h3 className="font-heading font-bold text-lg mt-4 tracking-tight break-anywhere">{f.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed break-anywhere">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Segunda CTA lojista */}
      <section className="relative z-10 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col items-center text-center gap-6">
          <div className="w-14 h-14 bg-foreground text-background flex items-center justify-center rounded-full">
            <Store size={24} />
          </div>
          <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl max-w-2xl leading-tight">
            Bem-vindo à nova experiência do lojista do VM Piabetá.
          </h2>
          <p className="text-muted-foreground max-w-xl">
            Um ambiente único que respeita seu tempo, sua rotina e sua operação. Acesse agora com o telefone cadastrado.
          </p>
          <Link to="/lojista/login">
            <Button data-testid="cta-final-lojista" size="lg" className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-14 px-10 text-base">
              Entrar no Portal do Lojista <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap justify-between items-center gap-4 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} VM Piabetá Shopping Center — Hub Digital do Lojista</div>
          <div className="flex items-center gap-4">
            <Link to="/admin/login" className="hover:text-foreground transition-colors" data-testid="footer-admin-link">
              Área da Administração
            </Link>
            <span className="font-mono">v1.0</span>
          </div>
        </div>
      </footer>
      <FloatingWhatsAppButton mensagemInicial="Olá! Vim pelo site do VM Piabetá Shopping e gostaria de mais informações." />
    </div>
  );
}
